<template>
   <div class="__container-landing flex min-h-screen flex-col gap-10 pt-20">
      <SlideUp :animate-in-view="false" class="rounded-medium overflow-hidden bg-white">
         <!-- Image -->
         <div v-if="locationImage" class="h-75 md:h-100">
            <Image :src="locationImage" :alt="locationName" class="size-full object-cover" />
         </div>

         <!-- Content -->
         <div class="flex flex-col gap-6 p-6 md:p-10">
            <!-- Map + Title -->
            <div class="flex flex-col gap-6 md:flex-row md:items-center">
               <div
                  v-if="mapFeatures"
                  class="h-50 md:h-50 md:w-100 w-full shrink-0 overflow-hidden rounded-lg"
               >
                  <Map :features="mapFeatures" readonly show-markers />
               </div>
               <div class="flex flex-col gap-3">
                  <TitleDescription
                     :title="locationName"
                     :description="organizationName"
                     heading="h1"
                  />
               </div>
            </div>

            <!-- CTA -->
         </div>
      </SlideUp>

      <SlideUp :animate-in-view="false" class="rounded-medium overflow-hidden bg-white">
         <div
            class="flex flex-col items-start justify-between gap-4 overflow-hidden p-6 md:flex-row md:items-center md:p-10"
         >
            <p class="p-l max-w-125 flex-1">
               {{ $t('web.pages.index.location.description') }}
            </p>
            <Stores apple-black />
         </div>
      </SlideUp>
   </div>
</template>

<script setup lang="ts">
import type { Database } from '@xleddyl/types'

const { locale, t } = useI18n()
const route = useRoute()

const client = useSupabaseClient<Database>()
const { data: location } = await useAsyncData(`location-${route.params.id}`, async () => {
   const { data } = await client
      .schema('web')
      .from('location')
      .select('*')
      .eq('id', route.params.id as string)
      .single()
      .throwOnError()
   return data
})

if (!location.value) {
   throw createError({ statusCode: 404, statusMessage: 'Location not found' })
}

const locationName = computed(() => {
   const name = location.value?.name
   return name?.[locale.value] || name?.it || ''
})

const locationDescription = computed(() => {
   const desc = location.value?.short_description
   return desc?.[locale.value] || desc?.it || ''
})

const organizationName = computed(() => {
   const name = location.value?.organization_name
   return name?.[locale.value] || name?.it || ''
})

const waterTypeLabel = computed(() => {
   const wt = location.value?.water_type
   if (!wt || wt === 'LWT_UNKNOWN' || wt === 'LWT_FORBIDDEN') return null
   return t(`enum.${wt}`)
})

const mapFeatures = computed(() => {
   const loc = location.value
   if (!loc?.geojson || JSON.stringify(loc.geojson) === '{}') return null
   return [
      {
         id: loc.id,
         name: locationName.value,
         geojson: loc.geojson,
         bbox: loc.bbox,
         center: loc.center,
      },
   ]
})

const locationImage = computed(() => {
   const images = location.value?.images_url
   if (!images?.length) return null
   const url = images[0]
   if (!url) return null
   if (url.startsWith('http')) return url
   const [bucket, ...path] = url.split('/')
   const { data } = client.storage.from(bucket).getPublicUrl(path.join('/'))
   return data.publicUrl || url
})

useSeo(
   t('web.seo.index.location.title', { location: locationName.value }),
   t('web.seo.index.location.description', { location: locationName.value })
)

useSchemaOrg([
   definePlace({
      name: locationName.value,
      description: locationDescription.value,
   }),
])
</script>
